import type { Region } from '@monthio/react-components'
import type { FunctionComponent } from 'react'

import {
	PrimaryButton as A11yPrimaryButton,
	BodyL,
	FlowPage,
	SecondaryButton,
} from '@monthio/design-system'
import { RegularButtonWrapper } from '@monthio/react-components'
import { getSmartCheckSessionOutputPdf } from '#api/smartcheckSessionApi'
import { flowFinishedCleanUp } from '#hooks/useFlowCleanupOnLeave'
import { intercomService } from '#services/intercomService'
import { useTranslation } from 'react-i18next'

import {
	ActionWrapper,
	DownLoadButtonsContainer,
} from '../FlowCompletedScreen.styled'
import { useFlowCompletedModule } from '../useFlowCompletedModule'
import { CoApplicantFlowImage } from './CoApplicantFlowImage'
import { DownloadButton } from './DownloadButton'
import { FinalFlowImage } from './FinalFlowImage'

export const MonthioFinishedScreen: FunctionComponent<{
	children?: never
}> = () => {
	const { t } = useTranslation()
	const {
		data: {
			hasIncomeVerification,
			mainApplicantId,
			shouldAllowOutputPdfDownload,
			shouldAllowRedirectToNextSession,
		},
		config: { clientName, regionId, isFinalFlow, partOfMultiFlow },
	} = useFlowCompletedModule()

	const params = new URLSearchParams(window.location.search)
	const demoProfile = params.get('demoProfile')
	const title = getTitle({
		isFinalFlow,
		partOfMultiFlow,
		shouldAllowOutputPdfDownload,
		hasIncomeVerification,
	})
	const pdfDownload = shouldAllowOutputPdfDownload

	const allowRedirectToMain = shouldAllowRedirectToNextSession
	if (!allowRedirectToMain) {
		intercomService.startEndOfFlowSurvey(regionId as Region)
	}

	const redirectToMainApplicant = () => {
		const demoProfileParam = demoProfile ? `&demoProfile=${demoProfile}` : ''
		flowFinishedCleanUp()
		window.location.search = `sessionId=${mainApplicantId}${demoProfileParam}`
	}

	return (
		<FlowPage>
			<FlowPage.Content $isCentered>
				{isFinalFlow ? (
					<>
						<FlowPage.Header
							slot={<FinalFlowImage />}
							title={t(title, {
								clientName,
							})}
						/>
						{pdfDownload && (
							<DownLoadButtonsContainer>
								<DownloadButton
									fetchBlob={getSmartCheckSessionOutputPdf}
									fileName="caseFile.pdf"
									startDownloadText={t(
										'flowCompletedScreen-downlaodPdfButton-download',
									)}
									failedDownloadText={t(
										'flowCompletedScreen-downlaodPdf-error',
									)}
								/>
							</DownLoadButtonsContainer>
						)}
					</>
				) : (
					<>
						<FlowPage.Header
							slot={<CoApplicantFlowImage />}
							title={t(title, {
								clientName,
							})}
						/>
						{allowRedirectToMain ? (
							<>
								<BodyL>
									{t('flowCompletedScreen-redirectToMain-explainer')}
								</BodyL>
								<ActionWrapper>
									<RegularButtonWrapper>
										<SecondaryButton
											width="full-width"
											onClick={() => {
												flowFinishedCleanUp()
												window.close()
											}}
										>
											{t('flowCompletedScreen-btn-close')}
										</SecondaryButton>
									</RegularButtonWrapper>
									<RegularButtonWrapper>
										<A11yPrimaryButton
											onClick={redirectToMainApplicant}
											width="full-width"
										>
											{t('flowCompletedScreen-btn-redirectToMain')}
										</A11yPrimaryButton>
									</RegularButtonWrapper>
								</ActionWrapper>
							</>
						) : null}
					</>
				)}
			</FlowPage.Content>
		</FlowPage>
	)
}

const getTitle = ({
	isFinalFlow,
	partOfMultiFlow,
	shouldAllowOutputPdfDownload,
	hasIncomeVerification,
}: {
	isFinalFlow: boolean
	partOfMultiFlow: boolean
	shouldAllowOutputPdfDownload: boolean
	hasIncomeVerification: boolean
}) => {
	if (hasIncomeVerification) {
		return 'flowCompletedScreen-incomeVerification-title'
	}

	if (isFinalFlow) {
		return partOfMultiFlow
			? 'flowCompletedScreen-completedByCoApplicants-title'
			: shouldAllowOutputPdfDownload
				? 'flowCompletedScreen-pfdDownload-title'
				: 'flowCompletedScreen-title'
	}

	return 'flowCompletedScreen-notCompletedByCoApplicant-title'
}
